// ─── Care Search Intelligence ─────────────────────────────────────────────────
// Maps patient search keywords to care service routes and suggestion types.

export type CareSearchResultType = 'doctor' | 'pharmacy' | 'lab' | 'imaging' | 'hospital' | 'ambulance' | 'insurance' | 'telemedicine';

export interface CareSearchResult {
  type: CareSearchResultType;
  route: string;
  label: string;
  query?: string;
  specialtyFilter?: string;
  urgent?: boolean;
  cautionMessage?: string;
}

// ─── Keyword Mapping Definitions ──────────────────────────────────────────────

interface KeywordMapping {
  keywords: string[];
  result: Omit<CareSearchResult, 'query'>;
}

const DOCTOR_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['dentist', 'dental', 'tooth', 'teeth'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Dentist', label: 'Dentist', specialtyFilter: 'Dentist' },
  },
  {
    keywords: ['heart', 'chest pain', 'cardiac'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Cardiologist', label: 'Cardiologist', specialtyFilter: 'Cardiologist', urgent: true, cautionMessage: 'If experiencing chest pain, call emergency immediately' },
  },
  {
    keywords: ['skin', 'acne', 'rash', 'derma'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Dermatologist', label: 'Dermatologist', specialtyFilter: 'Dermatologist' },
  },
  {
    keywords: ['child', 'pediatric', 'kids', 'baby'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Pediatrician', label: 'Pediatrician', specialtyFilter: 'Pediatrician' },
  },
  {
    keywords: ['bone', 'joint', 'orthopedic', 'fracture'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Orthopedist', label: 'Orthopedist', specialtyFilter: 'Orthopedist' },
  },
  {
    keywords: ['eye', 'vision', 'ophthalmol'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Ophthalmologist', label: 'Ophthalmologist', specialtyFilter: 'Ophthalmologist' },
  },
  {
    keywords: ['ent', 'ear', 'nose', 'throat', 'sinus'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=ENT', label: 'ENT', specialtyFilter: 'ENT' },
  },
  {
    keywords: ['neuro', 'headache', 'migraine'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Neurologist', label: 'Neurologist', specialtyFilter: 'Neurologist' },
  },
  {
    keywords: ['gynec', 'pregnancy', 'women'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Gynecologist', label: 'Gynecologist', specialtyFilter: 'Gynecologist' },
  },
  {
    keywords: ['fever', 'cold', 'flu', 'cough', 'general'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=General Physician', label: 'General Physician', specialtyFilter: 'General Physician' },
  },
  {
    keywords: ['mental', 'anxiety', 'depression', 'psych'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Psychiatrist', label: 'Psychiatrist', specialtyFilter: 'Psychiatrist' },
  },
  {
    keywords: ['physio', 'rehabilitation'],
    result: { type: 'doctor', route: '/patient/care-services/doctors?specialty=Physiotherapist', label: 'Physiotherapist', specialtyFilter: 'Physiotherapist' },
  },
  {
    keywords: ['doctor', 'specialist', 'consult'],
    result: { type: 'doctor', route: '/patient/care-services/doctors', label: 'All Doctors' },
  },
];

const LAB_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['cbc', 'blood test', 'blood count', 'hemoglobin'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=blood', label: 'Blood Tests' },
  },
  {
    keywords: ['thyroid', 'tsh', 't3', 't4'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=thyroid', label: 'Thyroid Tests' },
  },
  {
    keywords: ['sugar', 'glucose', 'hba1c', 'diabetes test'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=glucose', label: 'Glucose / Diabetes Tests' },
  },
  {
    keywords: ['lipid', 'cholesterol'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=lipid', label: 'Lipid Profile' },
  },
  {
    keywords: ['liver', 'lft', 'sgpt'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=liver', label: 'Liver Function Tests' },
  },
  {
    keywords: ['kidney', 'kft', 'creatinine', 'urea'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=kidney', label: 'Kidney Function Tests' },
  },
  {
    keywords: ['urine', 'urinalysis'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=urine', label: 'Urine Tests' },
  },
  {
    keywords: ['vitamin', 'b12', 'd3'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=vitamin', label: 'Vitamin Tests' },
  },
  {
    keywords: ['covid', 'rt-pcr', 'antigen'],
    result: { type: 'lab', route: '/patient/care-services/labs?search=covid', label: 'COVID Tests' },
  },
  {
    keywords: ['lab', 'test', 'pathology', 'diagnostic'],
    result: { type: 'lab', route: '/patient/care-services/labs', label: 'Lab Tests' },
  },
];

const IMAGING_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['xray', 'x-ray', 'x ray'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=X-Ray', label: 'X-Ray' },
  },
  {
    keywords: ['ct', 'ct scan', 'computed'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=CT Scan', label: 'CT Scan' },
  },
  {
    keywords: ['mri', 'magnetic'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=MRI', label: 'MRI' },
  },
  {
    keywords: ['ultrasound', 'usg', 'sonography'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=Ultrasound', label: 'Ultrasound' },
  },
  {
    keywords: ['pet', 'pet scan'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=PET Scan', label: 'PET Scan' },
  },
  {
    keywords: ['mammography', 'breast scan'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=Mammography', label: 'Mammography' },
  },
  {
    keywords: ['ecg', 'ekg', 'electrocardiogram'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=ECG', label: 'ECG' },
  },
  {
    keywords: ['echo', 'echocardiogram'],
    result: { type: 'imaging', route: '/patient/care-services/imaging?modality=2D Echo', label: '2D Echo' },
  },
  {
    keywords: ['scan', 'imaging', 'radiology'],
    result: { type: 'imaging', route: '/patient/care-services/imaging', label: 'Imaging & Radiology' },
  },
];

const PHARMACY_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['medicine', 'medication', 'drug', 'tablet', 'capsule', 'syrup'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy', label: 'Pharmacy' },
  },
  {
    keywords: ['paracetamol', 'crocin', 'dolo'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy?search=paracetamol', label: 'Paracetamol / Pain Relief' },
  },
  {
    keywords: ['amoxicillin', 'azithromycin', 'antibiotic'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy?search=antibiotic', label: 'Antibiotics' },
  },
  {
    keywords: ['insulin', 'metformin'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy?search=diabetes', label: 'Diabetes Medicines' },
  },
  {
    keywords: ['omeprazole', 'pantoprazole', 'antacid'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy?search=antacid', label: 'Antacids' },
  },
  {
    keywords: ['cetirizine', 'allegra', 'allergy'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy?search=allergy', label: 'Allergy Medicines' },
  },
  {
    keywords: ['pharmacy', 'chemist', 'medical store', 'order medicines'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy', label: 'Pharmacy' },
  },
  {
    keywords: ['prescription', 'rx'],
    result: { type: 'pharmacy', route: '/patient/care-services/pharmacy', label: 'Prescription Medicines' },
  },
];

const HOSPITAL_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['hospital', 'opd', 'admission', 'ward'],
    result: { type: 'hospital', route: '/patient/care-services/hospitals', label: 'Hospitals' },
  },
  {
    keywords: ['emergency room', 'er', 'icu'],
    result: { type: 'hospital', route: '/patient/care-services/hospitals', label: 'Emergency / ICU', urgent: true, cautionMessage: 'For life-threatening emergencies, call 108 or visit the nearest ER immediately' },
  },
];

const AMBULANCE_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['ambulance', 'sos', 'emergency'],
    result: { type: 'ambulance', route: '/patient/care-services/ambulance', label: 'Ambulance', urgent: true, cautionMessage: 'For life-threatening emergencies, call 108 immediately' },
  },
];

const INSURANCE_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['insurance', 'claim', 'preauth', 'pre-auth', 'policy', 'cashless', 'tpa'],
    result: { type: 'insurance', route: '/patient/care-services/insurance', label: 'Insurance' },
  },
];

const TELEMEDICINE_MAPPINGS: KeywordMapping[] = [
  {
    keywords: ['telemedicine', 'video consult', 'online doctor', 'virtual'],
    result: { type: 'telemedicine', route: '/patient/care-services/telemedicine', label: 'Telemedicine' },
  },
];

const ALL_MAPPINGS: KeywordMapping[] = [
  ...DOCTOR_MAPPINGS,
  ...LAB_MAPPINGS,
  ...IMAGING_MAPPINGS,
  ...PHARMACY_MAPPINGS,
  ...HOSPITAL_MAPPINGS,
  ...AMBULANCE_MAPPINGS,
  ...INSURANCE_MAPPINGS,
  ...TELEMEDICINE_MAPPINGS,
];

// ─── Search Function ──────────────────────────────────────────────────────────

export function searchCareServices(query: string): CareSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results: { result: CareSearchResult; score: number }[] = [];
  const seenRoutes = new Set<string>();

  for (const mapping of ALL_MAPPINGS) {
    let bestScore = 0;

    for (const keyword of mapping.keywords) {
      let score = 0;

      // Exact match with the full query
      if (q === keyword) {
        score = 100;
      }
      // Query contains the keyword exactly
      else if (q.includes(keyword)) {
        score = 80;
      }
      // Keyword contains the query (partial match)
      else if (keyword.includes(q)) {
        score = 60;
      }
      // Multi-word: any word in the query matches a keyword
      else {
        const words = q.split(/\s+/);
        for (const word of words) {
          if (word.length < 2) continue;
          if (keyword === word) {
            score = Math.max(score, 70);
          } else if (keyword.includes(word)) {
            score = Math.max(score, 40);
          } else if (word.includes(keyword)) {
            score = Math.max(score, 40);
          }
        }
      }

      bestScore = Math.max(bestScore, score);
    }

    if (bestScore > 0) {
      const route = mapping.result.route;
      if (!seenRoutes.has(route)) {
        seenRoutes.add(route);
        results.push({
          result: { ...mapping.result, query: q },
          score: bestScore,
        });
      }
    }
  }

  // Sort by relevance (highest score first), urgent results get a small boost
  results.sort((a, b) => {
    const aScore = a.score + (a.result.urgent ? 5 : 0);
    const bScore = b.score + (b.result.urgent ? 5 : 0);
    return bScore - aScore;
  });

  return results.map((r) => r.result);
}

// ─── Autocomplete Suggestions ─────────────────────────────────────────────────

const SUGGESTION_LABELS: string[] = [
  // Doctor specialties
  'dentist', 'dental checkup', 'cardiologist', 'heart specialist',
  'dermatologist', 'skin specialist', 'pediatrician', 'child specialist',
  'orthopedist', 'bone specialist', 'ophthalmologist', 'eye specialist',
  'ent specialist', 'neurologist', 'gynecologist', 'general physician',
  'psychiatrist', 'physiotherapist', 'doctor',
  // Lab tests
  'blood test', 'cbc', 'thyroid test', 'tsh', 'glucose test', 'hba1c',
  'lipid profile', 'cholesterol', 'liver function test', 'kidney function test',
  'urine test', 'vitamin d3', 'vitamin b12', 'covid test', 'lab test',
  // Imaging
  'x-ray', 'ct scan', 'mri', 'ultrasound', 'pet scan', 'mammography',
  'ecg', 'echocardiogram', 'imaging',
  // Pharmacy
  'pharmacy', 'order medicines', 'paracetamol', 'antibiotic', 'antacid',
  'allergy medicine', 'prescription',
  // Other services
  'hospital', 'ambulance', 'emergency', 'insurance', 'cashless',
  'telemedicine', 'video consult', 'online doctor',
];

export function getSearchSuggestions(partial: string): string[] {
  const p = partial.trim().toLowerCase();
  if (!p) return [];

  const scored: { label: string; score: number }[] = [];

  for (const label of SUGGESTION_LABELS) {
    let score = 0;

    if (label.startsWith(p)) {
      score = 100;
    } else if (label.includes(p)) {
      score = 60;
    } else {
      // Check individual words
      const words = p.split(/\s+/);
      for (const word of words) {
        if (word.length < 2) continue;
        if (label.includes(word)) {
          score = Math.max(score, 40);
        }
      }
    }

    if (score > 0) {
      scored.push({ label, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 5).map((s) => s.label);
}
